import {
  ComponentType,
  ReactComponents,
  ReactComponentType,
} from 'strapi/component/types';
import { PageType, PageTypes } from 'strapi/page/types';
import { ReactComponentBuildResponse, RequestParams } from 'strapi/types';

import {
  AiTextTool,
  aiTextToolParams,
  collectAiTextToolParams,
} from './aiTextTool';
import { BenefitsRow, claimsParams, collectClaimsParams } from './benefitsRow';
import {
  BottomBanner,
  bottomBannerParams,
  collectBottomBannerParams,
} from './bottomBanner';
import {
  BreadCrumb,
  breadcrumbsParams,
  collectBreadcrumbParams,
} from './breadcrumb';
import {
  collectTextImageTextParams,
  BulkText,
  textImageTextParams,
} from './bulkText';
import {
  Carousel,
  carouselParams,
  collectCarouselParams,
  enrichWithUseCaseImageParams,
} from './carousel';
import {
  CategoryList,
  categoryListParams,
  collectCategoryListParams,
  enrichWithCategoryListParams,
} from './categoryList';
import { childrenListParams, collectChildrenListParams } from './childrenList';
import {
  FrequentlyAskedQuestions,
  collectFrequentlyAskedQuestionsParams,
  frequentlyAskedQuestionsParams,
} from './frequentlyAskedQuestions';
import {
  collectHeaderBannerParams,
  HeaderBanner,
  headerBannerParams,
} from './headerBanner';
import {
  HeroSection,
  collectHeroSectionParams,
  heroSectionParams,
} from './heroSection';
import {
  ImageText,
  collectImageSectionParams,
  imageSectionParams,
} from './imageText';
import {
  collectImageSectionSingleParams,
  MediaSpotlight,
  imageSectionSingleParams,
} from './mediaSpotlight';
import {
  collectMockupsGroupParams,
  MockupsGroup,
  mockupsGroupParams,
} from './mockupsGroup';
import {
  RelatedLinks,
  collectRelatedLinksParams,
  relatedLinksComponentParams,
} from './relatedLinks';
import { collectSeoParams, SeoCard, seoComponentParams } from './seo';
import { Steps, collectStepsParams, stepsParams } from './steps';
import {
  collectTemplateFeedParams,
  TemplateFeed,
  templateFeedParams,
} from './templateFeed';
import {
  Testimonial,
  collectTestimonialParams,
  testimonialParams,
} from './testimonial';

// Step 1: Those build functions define what fields we need to populate
// in each component by component type.
// The object should be built from the base level of the page.
export const componentRequestParams: Record<
  ComponentType,
  (type: PageType) => RequestParams<PageTypes>
> = {
  [ComponentType.AiTextTool]: aiTextToolParams,
  [ComponentType.BottomBanner]: bottomBannerParams,
  [ComponentType.Breadcrumb]: breadcrumbsParams,
  [ComponentType.Carousel]: carouselParams,
  [ComponentType.CategoryList]: categoryListParams,
  [ComponentType.ChildrenList]: childrenListParams,
  [ComponentType.Claims]: claimsParams,
  [ComponentType.FrequentlyAskedQuestions]: frequentlyAskedQuestionsParams,
  [ComponentType.HeaderBanner]: headerBannerParams,
  [ComponentType.HeroSection]: heroSectionParams,
  [ComponentType.ImageSectionSingle]: imageSectionSingleParams,
  [ComponentType.ImageSection]: imageSectionParams,
  [ComponentType.MockupsGroup]: mockupsGroupParams,
  [ComponentType.RelatedLinks]: relatedLinksComponentParams,
  [ComponentType.Seo]: seoComponentParams,
  [ComponentType.Steps]: stepsParams,
  [ComponentType.TemplateFeed]: templateFeedParams,
  [ComponentType.Testimonial]: testimonialParams,
  [ComponentType.TextImageText]: textImageTextParams,
};

// Step 2: Those functions define how to enrich the resulted page object,
// that came from api in order to add more data or adjust data for specific component
// to prepare this page object to the step 3
export const componentParamsEnricher: Partial<
  Record<ComponentType, (type: PageType, page: PageTypes) => Promise<PageTypes>>
> = {
  [ComponentType.CategoryList]: enrichWithCategoryListParams,
  [ComponentType.Carousel]: enrichWithUseCaseImageParams,
};

// Step 2: Those collection functions define where to find props for React components by component type.
// The object should be from the base level of the page
export const componentParamsCollector: Record<
  ComponentType,
  (
    type: PageType,
    page: PageTypes
  ) =>
    | ReactComponentBuildResponse<ReactComponents>
    | ReactComponentBuildResponse<typeof ImageText>[]
    | undefined
> = {
  [ComponentType.AiTextTool]: collectAiTextToolParams,
  [ComponentType.BottomBanner]: collectBottomBannerParams,
  [ComponentType.Breadcrumb]: collectBreadcrumbParams,
  [ComponentType.Carousel]: collectCarouselParams,
  [ComponentType.CategoryList]: collectCategoryListParams,
  [ComponentType.ChildrenList]: collectChildrenListParams,
  [ComponentType.Claims]: collectClaimsParams,
  [ComponentType.FrequentlyAskedQuestions]:
    collectFrequentlyAskedQuestionsParams,
  [ComponentType.HeaderBanner]: collectHeaderBannerParams,
  [ComponentType.HeroSection]: collectHeroSectionParams,
  [ComponentType.ImageSectionSingle]: collectImageSectionSingleParams,
  [ComponentType.ImageSection]: collectImageSectionParams,
  [ComponentType.MockupsGroup]: collectMockupsGroupParams,
  [ComponentType.RelatedLinks]: collectRelatedLinksParams,
  [ComponentType.Seo]: collectSeoParams,
  [ComponentType.Steps]: collectStepsParams,
  [ComponentType.TemplateFeed]: collectTemplateFeedParams,
  [ComponentType.Testimonial]: collectTestimonialParams,
  [ComponentType.TextImageText]: collectTextImageTextParams,
};

export const reactComponentByType: Record<ReactComponentType, ReactComponents> =
  {
    [ReactComponentType.AiTextTool]: AiTextTool,
    [ReactComponentType.BottomBanner]: BottomBanner,
    [ReactComponentType.Breadcrumb]: BreadCrumb,
    [ReactComponentType.Carousel]: Carousel,
    [ReactComponentType.CategoryList]: CategoryList,
    [ReactComponentType.Claims]: BenefitsRow,
    [ReactComponentType.FrequentlyAskedQuestions]: FrequentlyAskedQuestions,
    [ReactComponentType.HeaderBanner]: HeaderBanner,
    [ReactComponentType.HeroSection]: HeroSection,
    [ReactComponentType.MediaSpotlight]: MediaSpotlight,
    [ReactComponentType.ImageSection]: ImageText,
    [ReactComponentType.MockupsGroup]: MockupsGroup,
    [ReactComponentType.RelatedLinks]: RelatedLinks,
    [ReactComponentType.SeoCard]: SeoCard,
    [ReactComponentType.Steps]: Steps,
    [ReactComponentType.TemplateFeed]: TemplateFeed,
    [ReactComponentType.Testimonial]: Testimonial,
    [ReactComponentType.TextImageText]: BulkText,
  };
